import { Injectable } from '@angular/core';
import { DocumentManagerService } from './document-manager.service';
import {
    ClockService,
    DocumentNumberToken,
    DocumentNumberingService,
    SalesDocumentType,
    SalesDocument,
    SalesDocumentPosition,
    IccDocumentPosition,
} from '@icc/b2b/shared';
import { core } from '@icc/shared/helpers/b2b';
import { ConfigService } from '@icc/shared';
import { DocumentPricesService } from './document-prices.service';
import { DocumentAuthor } from 'libs/b2b/shared/src/lib/models/document-author.model';
import * as Sentry from '@sentry/browser';
import { parseDate } from '@icc/shared/helpers';

@Injectable({
    providedIn: 'root',
})
export class DocumentCreationService {
    constructor(
        private configService: ConfigService,
        private documentManagerService: DocumentManagerService,
        private documentPricesService: DocumentPricesService
    ) {}

    async duplicateSalesAndBuyDocuments(
        salesDocument: SalesDocument,
        subDocument = false,
        isConfirmed = false,
        documentType: SalesDocumentType = salesDocument.salesDocumentTypeId,
        isOrderPlaced = false,
        allowEditBy: 'sales' | 'all' | null = null,
    ) {
        if (
            salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
            documentType === SalesDocumentType.Order &&
            subDocument
        ) {
            salesDocument.isAnnexed = true;
            this.documentManagerService.saveDocument(salesDocument.uuid, {
                isAnnexed: true,
            });
        }

        const isSellerLicensed = salesDocument?.sellerCompanyId === +this.configService.user.user.ActiveCompany &&
            this.configService.user.user.ActiveCompanyLicenseType === 'license';

        const { newDocument: newSalesDocument, positionsMap: salesPositionsMap, attachmentsMap: salesAttachmentsMap } =
            await this.duplicateDocument(
                salesDocument,
                subDocument,
                false,
                isConfirmed,
                documentType,
                salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation &&
                    documentType === SalesDocumentType.Order && !isSellerLicensed,
                false,
                !isOrderPlaced,
                null,
                allowEditBy,
            );

        const sourceBuyDocuments = await this.documentManagerService.getBuyDocuments(
            salesDocument.uuid
        );
        const connections = await this.documentManagerService.getConnectionsForDocument(
            salesDocument.uuid
        );
        const newBuyDocuments = [];
        for (const sourceBuyDocument of sourceBuyDocuments) {
            if (sourceBuyDocument.isEmpty) {
                continue;
            }
            if (
                salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation &&
                documentType === SalesDocumentType.Order
            ) {
                sourceBuyDocument.isOrderCreated = true;
                sourceBuyDocument.isOrderPlaced = isOrderPlaced;
                this.documentManagerService.saveDocument(sourceBuyDocument.uuid, {
                    isOrderCreated: true,
                    isOrderPlaced,
                });
            }
            const { newDocument: newBuyDocument, positionsMap: buyPositionsMap, attachmentsMap: buyAttachmentsMap } =
                await this.duplicateDocument(
                    sourceBuyDocument,
                    subDocument,
                    true,
                    isConfirmed,
                    documentType,
                    false,
                    false,
                    false,
                    null,
                    allowEditBy,
                );
            this.documentManagerService.joinDocuments(newSalesDocument.uuid, newBuyDocument.uuid);
            newBuyDocuments.push(newBuyDocument);
            if (connections[sourceBuyDocument.uuid]) {
                const buyPositionConnections = Object.values(connections[sourceBuyDocument.uuid]).filter(
                    (c) => c.salesPositionId
                );
                const buyAttachmentConnections = Object.values(connections[sourceBuyDocument.uuid]).filter(
                    (c) => c.salesAttachmentId
                );
                for (const connection of buyPositionConnections) {
                    this.documentManagerService.joinPositions(
                        newSalesDocument.uuid,
                        newBuyDocument.uuid,
                        salesPositionsMap.get(connection.salesPositionId),
                        buyPositionsMap.get(connection.buyPositionId)
                    );
                }
                for (const connection of buyAttachmentConnections) {
                    this.documentManagerService.joinAttachments(
                        newSalesDocument.uuid,
                        newBuyDocument.uuid,
                        salesAttachmentsMap.get(connection.salesAttachmentId),
                        buyAttachmentsMap.get(connection.buyAttachmentId)
                    );
                }
            }
        }
        return { sales: newSalesDocument, buy: newBuyDocuments };
    }

    async duplicateDocument(
        /**
         * Dokument do skopiowania
         */
        salesDocument: SalesDocument,
        /**
         * Czy kopiować jako poddokument?
         *
         * Nowy dokument będzie powiązany z dokumentem nadrzędnym, a także numeracja
         * będzie zależna od dokumentu nadrzędnego, zostanie nadany kolejny numer wersji.
         */
        subDocument = false,
        /**
         * Czy kopiowany dokument jest dokumentem zakupu?
         *
         * W przypadku kopiowania dokumentu zakupu, jego pozycje nie będą przeliczane
         * oraz jeśli jest kopiowane zamówienie jako zamówienie to nie zostanie od razu
         * "otwarte".
         */
        buySide = false,
        /**
         * Czy kopiowany dokument ma być od razu zatwierdzony?
         */
        isConfirmed = false,
        /**
         * Docelowy typ dokumentu
         */
        documentType: SalesDocumentType = salesDocument.salesDocumentTypeId,
        /**
         * Czy nowy dokument ma być od razu złożony jako zamówienie?
         *
         * Dotyczy tylko duplikowania oferty jako zamówienia.
         */
        isOrderPlaced = false,
        /**
         * Czy dokument został zmodyfikowany w trybie modyfikowania samej sprzedaży?
         */
        isConfirmedOnlySalesPositions = false,
        /**
         * Jeśli jest ustawione to zapisujemy zalogowaną osobę jako osobę,
         * która przygotowała zamówienie do zatwierdzenia.
         */
        orderLater = false,
        /**
         * Jeśli jest ustawione to zapisujemy przekazaną osobę jako autora kopii dokumentu.
         */
        author?: DocumentAuthor,
        /**
         * Możliwość edycji zamówienia przez: tylko sprzedawcę lub także kupującego. null - brak ograniczeń
         */
        allowedEditBy: 'sales' | 'all' | null = null,
    ) {
        const rootDocument = await this.documentManagerService.getDocument(salesDocument.rootId);
        const quotationDocument =
            salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation
                ? salesDocument
                : rootDocument;
        const { key: documentNumber } = DocumentNumberingService.generate(
            this.configService.user.documentNumberingScheme[
                DocumentNumberingService.getNumberingScheme(null, documentType)
            ],
            {
                buyerId: 0,
                client: salesDocument.client || {
                    reference: '',
                },
                sellerUserCode: await this.documentManagerService.getSellerUserCode(salesDocument),
                marketCode: this.configService.user.market,
                documentCustomCode: salesDocument.documentCustomCode && !buySide ? salesDocument.documentCustomCode : '',
                number:
                    subDocument && salesDocument.number != null ? String(salesDocument.number) : '',
                userId: this.configService.user.user.id,
                quotationNumber: quotationDocument?.documentNumber
                    ? quotationDocument.documentNumber.join('')
                    : DocumentNumberingService.getElementInDocumentNumber(
                          salesDocument.documentNumber,
                          DocumentNumberToken.O_ALL,
                          this.configService.user.documentNumberingScheme[
                              DocumentNumberingService.getNumberingScheme(null, documentType)
                          ]
                      ) || '',
                quotationNumberOrdinal:
                    quotationDocument?.number != null
                        ? String(quotationDocument.number)
                        : DocumentNumberingService.getElementInDocumentNumber(
                              salesDocument.documentNumber,
                              DocumentNumberToken.O_NUM,
                              this.configService.user.documentNumberingScheme[
                                  DocumentNumberingService.getNumberingScheme(null, documentType)
                              ]
                          ) ?? '',
                quotationVersion:
                    quotationDocument?.version != null
                        ? Number(quotationDocument.version || 0)
                        : Number(
                              DocumentNumberingService.getElementInDocumentNumber(
                                  salesDocument.documentNumber,
                                  DocumentNumberToken.O_V_DIGIT,
                                  this.configService.user.documentNumberingScheme[
                                      DocumentNumberingService.getNumberingScheme(null, documentType)
                                  ]
                              )
                          ) ??
                          '0abcdefghijklmnopqrstuvwxyz'.indexOf(
                              DocumentNumberingService.getElementInDocumentNumber(
                                  salesDocument.documentNumber,
                                  DocumentNumberToken.O_V_NUMBER,
                                  this.configService.user.documentNumberingScheme[
                                      DocumentNumberingService.getNumberingScheme(null, documentType)
                                  ]
                              )
                          ) ??
                          0,
                rootDocumentCreationDate: subDocument && salesDocument.rootCreated,
                creationDate:
                    ((documentType === SalesDocumentType.Order ||
                        documentType === SalesDocumentType.Inquiry) &&
                        salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation) ||
                    (salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation && !subDocument)
                        ? new Date()
                        : salesDocument.created,
            }
        );
        const flagsToReset = this.getFlagsToReset(salesDocument, subDocument, documentType);
        const newDocument: SalesDocument = {
            ...salesDocument,
            created: new Date(),
            modified: new Date(),
            uuid: core.generateUUID(),
            id: null,
            isAdvancePaid: false,
            isCanceled: false,
            isConfirmed,
            isConfirmedOnlySalesPositions,
            isDeleted: false,
            isDelivered: false,
            isOpened:
                documentType === SalesDocumentType.Order &&
                salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                subDocument &&
                !buySide
                    ? salesDocument.isOpened
                    : false,
            isOrderCreated: false,
            isOrderPlaced:
                documentType === SalesDocumentType.Order
                    ? salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation
                        ? isOrderPlaced
                        : false
                    : false,
            isTotalPaid: false,
            isAcceptedForProcessing: false,
            isInProgress: false,
            isCompleted: false,
            isCreatedBuyerDocument: this.configService.user.user.ActiveCompanyLicenseType !== 'license' || subDocument ? salesDocument.isCreatedBuyerDocument : false,
            isSentToBuyer: this.configService.user.user.ActiveCompanyLicenseType === 'license' && subDocument ? salesDocument.isSentToBuyer : false,
            salesDocumentTypeId: documentType,
            version: null,
            documentNumber,
            isAnnexed: false,
            number: subDocument ? salesDocument.number : null,
            parentId: null,
            rootId: null,
            flags: {
                ...salesDocument.flags,
                ...flagsToReset,
            },
            authorCompanyId: author?.companyId ?? 0,
            authorUserId: author?.userId ?? 0,
            author: author?.userId ? {
                companyId: author.companyId ?? 0,
                userId: author.userId,
                referenceName: author.referenceName ?? '',
                email: author.email ?? '',
                phoneNumber: author.phoneNumber ?? '',
            } : null,
            unplacedOrderUserId: orderLater ? this.configService.user.user.id : null,
            externalId: documentType !== SalesDocumentType.Order ? null : salesDocument.externalId,
            externalSource:
                documentType !== SalesDocumentType.Order ? null : salesDocument.externalSource,
            declaredDeadline: documentType === SalesDocumentType.Order &&
                salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                subDocument ? null : salesDocument.declaredDeadline,
            allowedEditBy: documentType === SalesDocumentType.Order &&
                subDocument &&
                allowedEditBy &&
                !isConfirmed
                    ? allowedEditBy : null,
        };
        const flagsToDelete = this.getFlagsToDelete(newDocument, salesDocument.salesDocumentTypeId, documentType);
        flagsToDelete.forEach((flag) => {
            delete newDocument.flags[flag];
        });

        if (subDocument) {
            newDocument.parentId = salesDocument.uuid;
            newDocument.rootId = salesDocument.rootId ?? salesDocument.uuid;
            newDocument.rootCreated = salesDocument.rootCreated ?? salesDocument.created;
        } else if (documentType === SalesDocumentType.Order || documentType === SalesDocumentType.Inquiry) {
            newDocument.parentId = salesDocument.uuid;
            newDocument.rootId = salesDocument.uuid;
            newDocument.rootCreated = salesDocument.rootCreated ?? salesDocument.created;
        }

        let sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(
            salesDocument.uuid
        );
        const sourceDocumentAttachments = await this.documentManagerService.getDocumentAttachments(
            salesDocument.uuid
        );

        if (
            documentType === SalesDocumentType.Inquiry &&
            salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation
        ) {
            sourceDocumentPositions = sourceDocumentPositions.filter(
                (position) =>
                    position.type !== 'defect' &&
                    (position.priceBeforeDiscountsValue === null ||
                    Number.isNaN(position.priceBeforeDiscountsValue) ||
                    typeof position.priceBeforeDiscountsValue === 'undefined')
            );
        }

        const attachmentIdsMap = new Map<string, string>();

        const positionIdsMap = new Map<string, string>();
        const positionsMap = new Map<string, IccDocumentPosition>();

        const newDocumentPositions = sourceDocumentPositions.sort((a, b) => {
            if (+parseDate(a.created) === +parseDate(b.created)) {
                return parseDate(a.rootCreated) < parseDate(b.rootCreated) ? -1 : 1;
            }
            return parseDate(a.created) < parseDate(b.created) ? -1 : 1;
        })
        .map((position) => {
            const { id, machineId, ...props } = position;
            const newUuid = core.generateUUID();
            positionIdsMap.set(position.uuid, newUuid);
            const newDocumentPosition: IccDocumentPosition = {
                ...props,
                uuid: newUuid,
                created: new Date(),
                modified: new Date(),
                rootCreated: props.rootCreated || props.created,
                documentId: newDocument.uuid,
                machineId: ClockService.getClock().timestamp.node(),
                parentId:
                    documentType === salesDocument.salesDocumentTypeId && subDocument
                        ? position.uuid
                        : null,
                rootId:
                    (documentType === salesDocument.salesDocumentTypeId && subDocument) ||
                    documentType === SalesDocumentType.Inquiry
                        ? position.rootId ?? position.uuid
                        : null,
            };
            positionsMap.set(position.uuid, newDocumentPosition);
            return newDocumentPosition;
        });

        for (const position of sourceDocumentPositions) {
            if (Object.keys(position.masters || {}).length > 0) {
                const masters = {};
                for (const master of Object.keys(position.masters)) {
                    if (positionIdsMap.get(master)) {
                        masters[positionIdsMap.get(master)] = position.masters[master];
                    }
                }
                positionsMap.get(position.uuid).masters = masters;
            }

            if (Object.keys(position.slaves || {}).length > 0) {
                const slaves = {};
                for (const fieldId of Object.keys(position.slaves)) {
                    const slave = position.slaves[fieldId];
                    if (slave && positionIdsMap.get(slave.positionId)) {
                        slaves[fieldId] = {
                            ...slave,
                            positionId: positionIdsMap.get(slave.positionId),
                        };
                    }
                }
                positionsMap.get(position.uuid).slaves = slaves;
            }
        }

        this.documentManagerService.addDocument(newDocument);
        newDocumentPositions.forEach((position) =>
            this.documentManagerService.addPosition(position, newDocument, true)
        );
        if (
            this.configService?.user?.offerRecalculationMethod === 'wholeOffer' &&
            (documentType !== SalesDocumentType.Order ||
                salesDocument.salesDocumentTypeId !== SalesDocumentType.Quotation) &&
            !buySide
        ) {
            this.documentPricesService.recalculatePosition(newDocument);
        }

        const newDocumentAttachments = sourceDocumentAttachments.map((attachment) => {
            const { id, machineId, ...props } = attachment;
            const newUuid = core.generateUUID();
            attachmentIdsMap.set(attachment.uuid, newUuid);
            return {
                ...props,
                uuid: newUuid,
                created: new Date(),
                modified: new Date(),
                documentId: newDocument.uuid,
                documentPositionId: props.documentPositionId
                    ? positionIdsMap.get(props.documentPositionId)
                    : null,
                machineId: ClockService.getClock().timestamp.node(),
                copyFromUuid: attachment.uuid,
            };
        });
        newDocumentAttachments.forEach((position) =>
            this.documentManagerService.addAttachment(position)
        );

        if (newDocumentAttachments.length > 3) {
            Sentry.captureMessage('Too many attachments added at once', {
                level: 'warning',
                extra: {
                    attachmentsCount: newDocumentAttachments.length,
                    salesDocumentId: newDocument.uuid,
                    files: newDocumentAttachments.map((file) => file.name),
                    attachments: newDocumentAttachments.map((file) => file.uuid),
                },
            });
        }

        return { newDocument, newDocumentPositions, positionsMap: positionIdsMap, attachmentsMap: attachmentIdsMap };
    }

    async duplicateSalesOrder(
        salesDocument: SalesDocument,
        onlyOwn = false,
        joinWithBuyDocument: SalesDocument | null = null,
        author?: DocumentAuthor,
        allowEditBy: 'sales' | 'all' | null = null,
    ) {
        let newSalesDocument: SalesDocument;
        let salesPositionsMap: Map<string, string>;
        let salesPositions: IccDocumentPosition[];
        let beenDuplicated = false;
        if (
            this.configService.config.config.Order.orderModificationWithinOneDocument &&
            salesDocument.salesDocumentTypeId === SalesDocumentType.Order
        ) {
            const result = await this.setModifiedDocument(
                salesDocument,
                false,
                salesDocument.salesDocumentTypeId,
                false,
                onlyOwn,
                allowEditBy
            );
            newSalesDocument = result.newDocument;
            salesPositionsMap = result.positionsMap;
            salesPositions = result.newDocumentPositions;
            beenDuplicated = false;
        } else {
            if (salesDocument.salesDocumentTypeId === SalesDocumentType.Order) {
                salesDocument.isAnnexed = true;
                this.documentManagerService.saveDocument(
                    salesDocument.uuid,
                    {
                        isAnnexed: true,
                    },
                    true
                );
            }

            const result = await this.duplicateDocument(
                salesDocument,
                true,
                false,
                false,
                salesDocument.salesDocumentTypeId,
                false,
                onlyOwn,
                false,
                author,
                allowEditBy,
            );
            newSalesDocument = result.newDocument;
            salesPositionsMap = result.positionsMap;
            salesPositions = result.newDocumentPositions;
            beenDuplicated = true;
            const connections = await this.documentManagerService.getConnectionsForDocument(
                salesDocument.uuid
            );
            const sourceBuyDocuments = await this.documentManagerService.getBuyDocuments(
                salesDocument.uuid
            );
            for (const sourceBuyDocument of sourceBuyDocuments) {
                let buyDocument = sourceBuyDocument;
                if (joinWithBuyDocument?.parentId === sourceBuyDocument.uuid) {
                    buyDocument = joinWithBuyDocument;
                }
                this.documentManagerService.joinDocuments(newSalesDocument.uuid, buyDocument.uuid);

                if (connections[sourceBuyDocument.uuid]) {
                    const buyPositionsMap = new Map<string, string>();
                    const sourceBuyDocumentPositions =
                        await this.documentManagerService.getDocumentPosition(buyDocument.uuid);
                    for (const position of sourceBuyDocumentPositions) {
                        buyPositionsMap.set(
                            joinWithBuyDocument?.parentId === sourceBuyDocument.uuid
                                ? position.parentId
                                : position.uuid,
                            position.uuid
                        );
                    }

                    const buyConnections = Object.values(
                        connections[sourceBuyDocument.uuid]
                    ).filter((c) => c.salesPositionId);
                    for (const connection of buyConnections) {
                        this.documentManagerService.joinPositions(
                            newSalesDocument.uuid,
                            buyDocument.uuid,
                            salesPositionsMap.get(connection.salesPositionId),
                            buyPositionsMap.get(connection.buyPositionId)
                        );
                    }
                }

                if (
                    sourceBuyDocument.parentId &&
                    salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation
                ) {
                    this.documentManagerService.detachDocuments(
                        salesDocument.uuid,
                        sourceBuyDocument.uuid
                    );
                }
            }
        }
        return { newSalesDocument, salesPositionsMap, beenDuplicated, salesPositions };
    }

    private getFlagsToReset(
        salesDocument: SalesDocument,
        subDocument: boolean,
        documentType: SalesDocumentType
    ) {
        if (salesDocument.salesDocumentTypeId === documentType) {
            return (
                (this.configService.user.statusSystems ?? {})[salesDocument.salesDocumentTypeId]
                    .flags ?? []
            )
                .filter((el) => {
                    if (subDocument) {
                        if (documentType === SalesDocumentType.Order) {
                            return el.resetAfterOrderAnnexation;
                        } else {
                            return el.resetAfterQuotationModification;
                        }
                    } else {
                        return el.resetAfterQuotationCopying;
                    }
                })
                .map((el) => el.code)
                .reduce((obj, el) => {
                    obj[el] = false;
                    return obj;
                }, {});
        } else {
            return {};
        }
    }

    private getFlagsToDelete(
        salesDocument: Partial<SalesDocument>,
        sourceDocumentType: SalesDocumentType,
        documentType: SalesDocumentType
    ) {
        if (sourceDocumentType === documentType) {
            return [];
        }
        const flagsToDelete = [];
        const availableFlags = new Map<string, boolean>();
        ((this.configService.user.statusSystems ?? {})[documentType]
            .flags ?? []
        ).forEach((flag) => {
            availableFlags.set(flag.code, true);
        });
        Object.keys(salesDocument.flags).forEach((flag) => {
            if (!availableFlags.has(flag)) {
                flagsToDelete.push(flag);
            }
        });
        return flagsToDelete;
    }

    async setModifiedDocument(
        salesDocument: SalesDocument,
        isConfirmed = false,
        documentType: SalesDocumentType = salesDocument.salesDocumentTypeId,
        isOrderPlaced = false,
        isConfirmedOnlySalesPositions = false,
        allowEditBy: 'sales' | 'all' | null = null,
    ) {
        const flagsToReset = this.getFlagsToReset(salesDocument, true, documentType);
        const changes: Partial<SalesDocument> = {
            isAdvancePaid: false,
            isCanceled: false,
            isConfirmed,
            isConfirmedOnlySalesPositions,
            isDeleted: false,
            isDelivered: false,
            isOpened:
                documentType === SalesDocumentType.Order &&
                salesDocument.salesDocumentTypeId === SalesDocumentType.Order &&
                salesDocument.isOpened,
            isOrderCreated: false,
            isOrderPlaced:
                documentType === SalesDocumentType.Order
                    ? salesDocument.salesDocumentTypeId === SalesDocumentType.Quotation
                        ? isOrderPlaced
                        : false
                    : false,
            isSentToBuyer: false,
            isTotalPaid: false,
            isAcceptedForProcessing: false,
            isInProgress: false,
            isCompleted: false,
            isAnnexed: false,
            flags: {
                ...salesDocument.flags,
                ...flagsToReset,
            },
            allowedEditBy: documentType === SalesDocumentType.Order && isConfirmed && allowEditBy ? allowEditBy : null,
        };
        const flagsToDelete = this.getFlagsToDelete(changes, salesDocument.salesDocumentTypeId, documentType);
        flagsToDelete.forEach((flag) => {
            delete changes.flags[flag];
        });
        Object.assign(salesDocument, changes);
        this.documentManagerService.saveDocument(salesDocument.uuid, changes, true);
        const sourceDocumentPositions = await this.documentManagerService.getDocumentPosition(
            salesDocument.uuid
        );
        const positionsMap = new Map<string, string>();
        sourceDocumentPositions.forEach((position) => {
            positionsMap.set(position.uuid, position.uuid);
        });

        return {
            newDocument: salesDocument,
            positionsMap,
            newDocumentPositions: sourceDocumentPositions,
        };
    }

    getMaxVersion(documents: SalesDocument[], documentType: SalesDocumentType) {
        const maxVersion = Math.max(...documents.map((doc) => Number(doc.version) ?? 0));
        let count = documents.length;
        if (documentType !== SalesDocumentType.Quotation) {
            // Pierwsza oferta nie ma rootId, w przeciwieństwie do innych typów dokumentów
            count = count - 1;
        }
        return Math.max(maxVersion, count) + 1;
    }
}
